import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ChevronLeft } from 'lucide-react';
import { login } from '@kss/auth';
import { authService, settingsService } from '@kss/api';
import { useConfig } from '@kss/theme';
import { Button, useToast, cn, TheBackyardLogo, FieldError, useField, v, onlyDigits, applyGuard } from '@kss/ui';

// Built-in default; overridden by the admin-set banner (Settings → Login banner) when present.
const HERO_FALLBACK = 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=800&q=75';

/**
 * Customer onboarding — Welcome/Login (mobile number → Get OTP) then OTP
 * verify. OTP-based, so the same flow logs in returning users and registers
 * new ones (per the design's Onboarding & Staff Login screen). Mocked until
 * `/api/auth/send-otp` · `/api/auth/verify-otp` are wired.
 */
export default function Login() {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resend, setResend] = useState(0);
  const [devCode, setDevCode] = useState('');
  const [flash, setFlash] = useState(false); // brief highlight when the dev OTP auto-fills
  const [name, setName] = useState(''); // optional — names the customer on their orders/bills
  const [busy, setBusy] = useState(false);
  const [hero, setHero] = useState(HERO_FALLBACK);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // The admin-set login banner (Web API), falling back to the built-in default.
  useEffect(() => {
    settingsService
      .getOutlet()
      .then((s) => {
        if (s.bannerImage) setHero(s.bannerImage);
      })
      .catch(() => {});
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { brand, logo } = useConfig(); // outlet name + uploaded logo (Settings) drive the wordmark/mark

  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
  const digits = phone.replace(/\D/g, '');

  // Same rules the API applies to these fields, so a number the form accepts is a number
  // verify-otp accepts. Errors surface on blur; the buttons gate on `problem` so they are
  // disabled before the field has been touched at all.
  const phoneField = useField(phone, [v.required('Mobile number'), v.mobile('Mobile number')]);
  const nameField = useField(name, [
    v.required('Name'),
    v.minLength(2, 'Name'),
    v.maxLength(60, 'Name'),
    v.safeName('Name'),
    ...v.safeText('Name')
  ]);
  const code = otp.join('');
  const otpComplete = code.length === 6;

  useEffect(() => {
    if (resend <= 0) return;
    const id = window.setInterval(() => setResend((r) => (r <= 1 ? 0 : r - 1)), 1000);
    return () => window.clearInterval(id);
  }, [resend]);

  async function sendOtp(e?: React.FormEvent) {
    e?.preventDefault();
    // Mark touched so the message appears under the field rather than only as a toast — a toast
    // names no field, which is survivable with one input and useless with six.
    phoneField.markTouched();
    if (phoneField.problem) return;
    setBusy(true);
    try {
      // The code is deliberately NOT read from the response, displayed or auto-filled.
      // It used to be toasted as "Dev OTP: …", printed under the boxes and typed into them
      // automatically — which meant anyone could sign in as any customer just by entering
      // their mobile number, and the "verification" verified nothing. The customer must
      // enter the code they received. (The backend still returns it; that needs removing
      // server-side too — the client no longer relies on it.)
      await authService.sendOtp(digits);
      setStep('otp');
      setOtp(['', '', '', '', '', '']);
      setResend(24);
      toast.show('Code sent to your mobile', 'info');
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch {
      toast.show('Could not send the code, try again', 'error');
    } finally {
      setBusy(false);
    }
  }

  function onOtpChange(i: number, val: string) {
    const d = val.replace(/\D/g, '').slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[i] = d;
      return next;
    });
    if (d && i < 5) otpRefs.current[i + 1]?.focus();
  }

  function onOtpKey(i: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  }

  async function verify() {
    nameField.markTouched();
    if (nameField.problem) return;
    if (!otpComplete) {
      toast.show('Enter the 6-digit code', 'error');
      return;
    }
    setBusy(true);
    try {
      const auth = await authService.verifyOtp(digits, code, name.trim());
      dispatch(
        login({
          user: {
            id: auth.id,
            name: auth.name,
            initials: auth.initials,
            phone: auth.phone,
            tier: auth.tier,
            points: auth.points
          },
          session: { token: auth.token, tenantId: 'thalios' }
        })
      );
      toast.show('Logged in', 'success');
      navigate(from, { replace: true });
    } catch {
      toast.show('Invalid or expired code', 'error');
      setBusy(false);
    }
  }

  return (
    <div className='flex min-h-screen items-stretch justify-center bg-surface-content font-sans text-content-primary sm:items-center sm:py-8'>
      <div className='flex w-full max-w-[440px] flex-col overflow-hidden bg-surface-card sm:rounded-3xl sm:shadow-pop'>
        {step === 'phone' ? (
          <>
            {/* Hero */}
            <div
              className='relative h-[300px] flex-shrink-0'
              style={{
                backgroundImage: `url(${hero})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(12,16,21,.25)_0%,rgba(12,16,21,0)_30%,rgba(12,16,21,.9)_100%)]' />
              <div className='absolute inset-x-6 bottom-6 text-white'>
                <TheBackyardLogo size={38} badge brand={brand} src={logo || undefined} className='mb-3 text-white' />
                <div className='font-display text-[26px] font-extrabold leading-tight'>
                  Sip. Relax.
                  <br />
                  Enjoy every bite.
                </div>
                <div className='mt-1.5 text-[13px] opacity-90'>
                  Good food, good mood &amp; good company.
                </div>
              </div>
            </div>

            {/* Login form */}
            <form onSubmit={sendOtp} className='flex flex-1 flex-col p-6'>
              <div className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
                Mobile number
              </div>
              <div
                className={cn(
                  'flex items-center gap-3 rounded-xl border-[1.5px] px-4 py-3.5',
                  phoneField.error ? 'border-danger' : 'border-border focus-within:border-primary'
                )}
              >
                <span className='font-display text-sm font-bold'>+91</span>
                <span className='h-4 w-px bg-border' />
                <input
                  value={phone}
                  // Guarded rather than free — a pasted "+91 98765-43210" becomes the 10 digits
                  // the API wants instead of failing validation for punctuation the user can't see.
                  onChange={(e) => setPhone(onlyDigits(e.target.value).slice(0, 10))}
                  onBlur={phoneField.onBlur}
                  inputMode='numeric'
                  maxLength={10}
                  autoFocus
                  aria-label='Mobile number'
                  aria-invalid={phoneField.error ? true : undefined}
                  placeholder='98765 43210'
                  className='w-full bg-transparent text-sm font-semibold tracking-wide text-content-primary placeholder:font-normal placeholder:tracking-normal placeholder:text-content-muted focus:outline-none'
                />
              </div>
              <FieldError error={phoneField.error} />
              <Button type='submit' block size='lg' className='mt-3.5' disabled={busy || !phoneField.isValid}>
                {busy ? 'Sending…' : 'Get OTP →'}
              </Button>

              <div className='mt-6 text-center text-[10.5px] leading-relaxed text-content-muted'>
                By continuing you agree to our{' '}
                <b className='text-content-secondary'>Terms</b> &amp;{' '}
                <b className='text-content-secondary'>Privacy Policy</b>
              </div>
              <Link
                to='/staff'
                className='mt-3 text-center text-[12px] font-semibold text-content-secondary hover:text-primary'
              >
                Restaurant staff? Staff login →
              </Link>
            </form>
          </>
        ) : (
          <div className='flex flex-1 flex-col p-6'>
            <button
              onClick={() => setStep('phone')}
              aria-label='Back'
              className='mb-2 -ml-1 flex h-9 w-9 items-center justify-center text-content-secondary'
            >
              <ChevronLeft size={22} />
            </button>
            <div className='font-display text-2xl font-extrabold'>Verify your number</div>
            <div className='mt-2 text-[13px] leading-relaxed text-content-secondary'>
              Enter the 6-digit code sent to
              <br />
              <b className='text-content-primary'>+91 {digits}</b> ·{' '}
              <button onClick={() => setStep('phone')} className='font-semibold text-primary'>
                Change
              </button>
            </div>

            {/* Name (required) — so the customer's orders/bills show who they are (mobile is the +91 above). */}
            <div className='mt-5'>
              <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
                Your name <span className='text-danger'>*</span>
              </div>
              <input
                value={name}
                onChange={(e) => setName(applyGuard(e.target.value, 'name', 60))}
                onBlur={nameField.onBlur}
                placeholder='e.g. Priya'
                maxLength={60}
                aria-label='Your name'
                aria-invalid={nameField.error ? true : undefined}
                className={cn(
                  'w-full rounded-xl border-[1.5px] px-4 py-3 text-sm font-medium focus:outline-none',
                  nameField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                )}
              />
              <FieldError error={nameField.error} />
            </div>

            <div className='mt-6 grid grid-cols-6 gap-2'>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  value={d}
                  onChange={(e) => onOtpChange(i, e.target.value)}
                  onKeyDown={(e) => onOtpKey(i, e)}
                  inputMode='numeric'
                  maxLength={1}
                  className={cn(
                    'h-14 w-full rounded-xl border-[1.5px] text-center font-display text-xl font-bold text-content-primary transition focus:outline-none',
                    d ? 'border-primary' : 'border-border focus:border-primary',
                    flash && 'motion-safe:animate-pulse bg-primary-light ring-2 ring-primary/50'
                  )}
                />
              ))}
            </div>

            <div className='mt-5 text-center text-[12.5px] text-content-muted'>
              {resend > 0 ? (
                <>
                  Resend code in{' '}
                  <b className='text-content-secondary'>0:{String(resend).padStart(2, '0')}</b>
                </>
              ) : (
                <button onClick={() => sendOtp()} className='font-semibold text-primary'>
                  Resend code
                </button>
              )}
            </div>

            <Button block size='lg' className='mt-5' onClick={verify} disabled={busy || !nameField.isValid || !otpComplete}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
